import { useCallback, useEffect, useRef, useState } from 'react';
import { Building2, ChevronDown, Check, Layers, Search } from 'lucide-react';

/**
 * OrgSwitcher — org dropdown directly under the team switcher.
 *
 * Mirrors TeamSwitcher's UX: a button row collapses to a popover panel
 * when tapped. Inside the panel: an "All orgs" entry on top, then every
 * org the user can act on inside the active team. Selecting an org sets
 * the oak-active-org cookie and reloads so list endpoints re-fetch under
 * the new scope. "All orgs" clears the cookie.
 *
 * Visibility scope (from /api/me/accessible-orgs):
 *   - Team owner / admin → every org in the active team
 *   - Team member       → only orgs where they're in the org's
 *                         approver_group_members
 *
 * The component hides itself if the active team has zero accessible orgs.
 */

interface ApiOrg {
  id: string;
  name: string | null;
  external_id: string;
  role: 'owner' | 'admin' | 'approver_1' | 'approver_2' | 'approver_both';
  pending_count: number;
}
interface ApiTeam {
  id: string;
  name: string | null;
  role: 'owner' | 'admin' | 'member';
  pending_count: number;
  orgs: ApiOrg[];
}
interface AccessibleResponse {
  teams: ApiTeam[];
  active_team_id: string | null;
  active_org_id: string | null;
}

function getCookie(name: string): string | undefined {
  if (typeof document === 'undefined') return undefined;
  const m = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
  return m ? decodeURIComponent(m[2]) : undefined;
}

function orgLabel(o: ApiOrg): string {
  return o.name || o.external_id || 'Org';
}

function roleLabel(role: ApiOrg['role']): string {
  if (role === 'owner') return 'Team owner';
  if (role === 'admin') return 'Team admin';
  if (role === 'approver_both') return 'Approver L1 + L2';
  if (role === 'approver_1') return 'Approver L1';
  return 'Approver L2';
}

export default function OrgSwitcher({ collapsed }: { collapsed: boolean }) {
  const [data, setData] = useState<AccessibleResponse | null>(null);
  const [activeOrgId, setActiveOrgId] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  // Search within the org list (a large fleet is unusable without it).
  const [query, setQuery] = useState('');
  const rootRef = useRef<HTMLDivElement>(null);

  const loadAccessibleOrgs = useCallback(() => {
    fetch('/api/me/accessible-orgs')
      .then((r) => (r.ok ? r.json() : null))
      .then((d: AccessibleResponse | null) => {
        if (!d) return;
        setData(d);
        setActiveOrgId(d.active_org_id || getCookie('oak-active-org') || null);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { loadAccessibleOrgs(); }, [loadAccessibleOrgs]);

  // Refresh own badge counts when something elsewhere changes the active
  // org (e.g. an approval got decided, badge counts shift).
  useEffect(() => {
    function onChange() {
      loadAccessibleOrgs();
    }
    window.addEventListener('oak-active-org-changed', onChange);
    return () => window.removeEventListener('oak-active-org-changed', onChange);
  }, [loadAccessibleOrgs]);

  // Close on outside click — same pattern as TeamSwitcher.
  useEffect(() => {
    function handler(e: MouseEvent) {
      if (!rootRef.current) return;
      if (!rootRef.current.contains(e.target as Node)) setOpen(false);
    }
    if (open) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // Reset the search box each time the panel closes.
  useEffect(() => { if (!open) setQuery(''); }, [open]);

  if (loading || !data || data.teams.length === 0) return null;

  // Active team is whatever the team-context cookie names; fall back to the
  // first accessible team. Team switching is handled by TeamSwitcher; we
  // just read the cookie it sets.
  const teamCookie = getCookie('oak-team-context');
  const activeTeam =
    data.teams.find((t) => t.id === teamCookie) || data.teams[0];

  if (activeTeam.orgs.length === 0) return null;

  // Soft-refresh pattern: set the cookie server-side, then dispatch a
  // window event so any mounted list page re-fetches. Replaces the
  // window.location.reload() approach which had a noticeable delay.
  // Pages that aren't listening yet won't re-fetch — that's a known gap
  // every page should subscribe; see the useActiveOrgChange hook.
  function notifyActiveOrgChanged(orgId: string | null) {
    window.dispatchEvent(
      new CustomEvent('oak-active-org-changed', { detail: { orgId } })
    );
  }

  async function handleOrgClick(org: ApiOrg) {
    setActiveOrgId(org.id);
    setOpen(false);
    try {
      await fetch('/api/me/active-org', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ org_id: org.id }),
      });
    } catch {}
    notifyActiveOrgChanged(org.id);
  }

  async function handleAllClick() {
    setActiveOrgId(null);
    setOpen(false);
    try {
      await fetch('/api/me/active-org', { method: 'DELETE' });
    } catch {}
    notifyActiveOrgChanged(null);
  }

  const showingAll = activeOrgId === null;
  const activeOrg = showingAll ? null : activeTeam.orgs.find((o) => o.id === activeOrgId);
  const allOrgsPending = activeTeam.orgs.reduce((sum, o) => sum + o.pending_count, 0);

  // Trigger label: "All orgs" or the active org's name. No role line here —
  // the org's role is redundant with the team-level role shown above and
  // adds noise to a frequently-tapped row.
  const triggerLabel = showingAll ? 'All orgs' : (activeOrg ? orgLabel(activeOrg) : 'All orgs');
  const triggerSub = showingAll
    ? `${activeTeam.orgs.length} org${activeTeam.orgs.length === 1 ? '' : 's'}`
    : null;
  const triggerBadge = showingAll ? allOrgsPending : (activeOrg?.pending_count ?? 0);
  const TriggerIcon = showingAll ? Layers : Building2;

  return (
    <div
      ref={rootRef}
      style={{
        padding: collapsed ? '0.5rem' : '0.75rem 1rem',
        borderBottom: '1px solid var(--border-color)',
        position: 'relative',
      }}
    >
      {!collapsed && (
        <div
          style={{
            fontSize: '0.6875rem',
            color: 'var(--text-tertiary)',
            textTransform: 'uppercase',
            letterSpacing: '0.04em',
            marginBottom: '0.375rem',
          }}
        >
          Org
        </div>
      )}
      <button
        onClick={() => setOpen((v) => !v)}
        title={collapsed ? triggerLabel : undefined}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: collapsed ? 0 : '0.625rem',
          width: '100%',
          padding: collapsed ? '0.5rem' : '0.5rem 0.625rem',
          background: open ? 'var(--bg-tertiary)' : 'transparent',
          border: '1px solid var(--border-color)',
          borderRadius: 'var(--radius-md)',
          cursor: 'pointer',
          color: 'var(--text-primary)',
          fontSize: '0.8125rem',
          fontWeight: 500,
          textAlign: 'left',
          justifyContent: collapsed ? 'center' : 'flex-start',
        }}
      >
        <TriggerIcon size={15} style={{ flexShrink: 0, color: 'var(--text-secondary)' }} />
        {!collapsed && (
          <>
            <div style={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
              <div
                style={{
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  lineHeight: 1.2,
                }}
              >
                {triggerLabel}
              </div>
              {triggerSub && (
                <div
                  style={{
                    fontSize: '0.6875rem',
                    color: 'var(--text-tertiary)',
                    fontWeight: 400,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    lineHeight: 1.2,
                  }}
                >
                  {triggerSub}
                </div>
              )}
            </div>
            {triggerBadge > 0 && (
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minWidth: '1.125rem',
                  height: '1.125rem',
                  padding: '0 0.375rem',
                  borderRadius: '999px',
                  background: 'var(--warning-text)',
                  color: 'white',
                  fontSize: '0.625rem',
                  fontWeight: 700,
                  flexShrink: 0,
                }}
              >
                {triggerBadge > 99 ? '99+' : triggerBadge}
              </span>
            )}
            <ChevronDown
              size={14}
              style={{
                flexShrink: 0,
                color: 'var(--text-tertiary)',
                transform: open ? 'rotate(180deg)' : 'none',
                transition: 'transform 0.15s',
              }}
            />
          </>
        )}
      </button>

      {open && !collapsed && (
        <div
          style={{
            position: 'absolute',
            top: '100%',
            left: '1rem',
            right: '1rem',
            marginTop: '0.25rem',
            background: 'var(--bg-primary)',
            border: '1px solid var(--border-color)',
            borderRadius: 'var(--radius-md)',
            boxShadow: '0 10px 30px rgba(0, 0, 0, 0.15)',
            zIndex: 100,
            maxHeight: 320,
            overflowY: 'auto',
          }}
        >
          {/* Search box (sticky) — always shown so the org list is filterable. */}
          {activeTeam.orgs.length > 1 && (
            <div style={{ position: 'sticky', top: 0, background: 'var(--bg-primary)', borderBottom: '1px solid var(--border-color)', padding: '0.4rem' }}>
              <div style={{ position: 'relative' }}>
                <Search size={14} style={{ position: 'absolute', left: '0.55rem', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-tertiary)', pointerEvents: 'none' }} />
                <input
                  autoFocus
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search orgs…"
                  className="form-input"
                  style={{ width: '100%', paddingLeft: '1.9rem', fontSize: '0.8rem' }}
                />
              </div>
            </div>
          )}
          {/* "All orgs" — always first so users can clear the filter in one click.
              Hidden while actively searching (it isn't a search result). */}
          {query.trim() === '' && (
            <DropdownItem
              icon={Layers}
              label="All orgs"
              sub={`${activeTeam.orgs.length} org${activeTeam.orgs.length === 1 ? '' : 's'}`}
              badge={allOrgsPending}
              active={showingAll}
              onClick={handleAllClick}
            />
          )}
          {activeTeam.orgs
            .filter((o) => {
              const q = query.trim().toLowerCase();
              if (!q) return true;
              return orgLabel(o).toLowerCase().includes(q) || (o.external_id || '').toLowerCase().includes(q);
            })
            .map((o) => (
              <DropdownItem
                key={o.id}
                icon={Building2}
                label={orgLabel(o)}
                sub={null}
                badge={o.pending_count}
                active={o.id === activeOrgId}
                onClick={() => handleOrgClick(o)}
              />
            ))}
          {query.trim() !== '' &&
            activeTeam.orgs.filter((o) => {
              const q = query.trim().toLowerCase();
              return orgLabel(o).toLowerCase().includes(q) || (o.external_id || '').toLowerCase().includes(q);
            }).length === 0 && (
              <div style={{ padding: '0.75rem', fontSize: '0.8rem', color: 'var(--text-tertiary)', textAlign: 'center' }}>No matches</div>
            )}
        </div>
      )}
    </div>
  );
}

function DropdownItem({
  icon: Icon,
  label,
  sub,
  badge,
  active,
  onClick,
}: {
  icon: typeof Building2;
  label: string;
  sub: string | null;
  badge: number;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem',
        width: '100%',
        padding: '0.625rem 0.75rem',
        background: active ? 'var(--bg-tertiary)' : 'transparent',
        border: 'none',
        borderBottom: '1px solid var(--border-color)',
        cursor: 'pointer',
        textAlign: 'left',
        color: 'var(--text-primary)',
        fontSize: '0.8125rem',
      }}
      onMouseEnter={(e) => {
        if (!active) e.currentTarget.style.background = 'var(--bg-secondary)';
      }}
      onMouseLeave={(e) => {
        if (!active) e.currentTarget.style.background = 'transparent';
      }}
    >
      <Icon size={16} style={{ flexShrink: 0, color: 'var(--brand-primary)' }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            fontWeight: 500,
          }}
        >
          {label}
        </div>
        {sub && (
          <div
            style={{
              fontSize: '0.6875rem',
              color: 'var(--text-tertiary)',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {sub}
          </div>
        )}
      </div>
      {badge > 0 && (
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            minWidth: '1.125rem',
            height: '1.125rem',
            padding: '0 0.375rem',
            borderRadius: '999px',
            background: 'var(--warning-text)',
            color: 'white',
            fontSize: '0.625rem',
            fontWeight: 700,
            flexShrink: 0,
          }}
        >
          {badge > 99 ? '99+' : badge}
        </span>
      )}
      {active && <Check size={14} style={{ color: 'var(--brand-primary)', flexShrink: 0 }} />}
    </button>
  );
}
